import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Hero from "@/components/Hero";
import FAQ from "@/components/FAQ";
import Marquee from "@/components/Marquee";
import BookingCTA from "@/components/BookingCTA";
import { Manifesto, EditorialSplit } from "@/components/Editorial";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { motion } from "framer-motion";
import { HERO_IMG, PHOTO } from "@/data/site";

const treatments = [
    {
        n: "01",
        title: "Manicure e Pedicure",
        text: "Mani curate e piedi in salute sono il risultato di trattamenti eseguiti con precisione e attenzione. Un servizio accurato e studiato sulle tue necessità.",
        services: ["Manicure", "Pedicure"],
        img: PHOTO.mani,
        color: "bg-rose-powder/70",
    },
    {
        n: "02",
        title: "Depilazione Donna",
        text: "Una pelle liscia e curata contribuisce a farti sentire bene in ogni stagione. Trattamenti eseguiti con delicatezza e attenzione.",
        services: [
            "Baffetto",
            "Sopracciglia",
            "Depilazione Braccia",
            "Depilazione Inguine Completo",
            "Depilazione Completa",
        ],
        img: PHOTO.curaPelle,
        color: "bg-blush/70",
    },
    {
        n: "03",
        title: "Trattamenti Viso",
        text: "Una pelle luminosa e curata nasce dalla costanza. Ti aiutiamo a scegliere il percorso ideale per valorizzare il tuo viso nel tempo.",
        services: ["Pulizia Viso", "Trattamento Viso Anti Age"],
        img: PHOTO.puliziaViso,
        color: "bg-champagne/70",
    },
    {
        n: "04",
        title: "Benessere del Corpo",
        text: "Concediti un momento dedicato esclusivamente a te. I nostri trattamenti aiutano a ritrovare relax e leggerezza.",
        services: ["Massaggio Rilassante", "Scrub Corpo"],
        img: PHOTO.donnaRilassata2,
        color: "bg-sage/40",
    },
];

const faq = [
    {
        q: "Ogni trattamento è personalizzato?",
        a: "Sì. Ogni cliente riceve una consulenza dedicata per individuare il percorso più adatto alle proprie esigenze.",
    },
    {
        q: "Quali servizi posso trovare?",
        a: "Manicure, pedicure, depilazione, pulizia viso, trattamento anti-age, massaggio rilassante, scrub corpo e molto altro.",
    },
    {
        q: "È necessario prenotare?",
        a: "Sì, la prenotazione è consigliata per permetterci di dedicare a ogni cliente il tempo necessario.",
    },
    {
        q: "Dove si trova Spazio Isa?",
        a: "In Via A. Minghi 30, nel quartiere Coteto, a Livorno.",
    },
];

export default function Estetica() {
    return (
        <>
            <Hero
                eyebrow="Oltre 15 anni nella cura della bellezza femminile"
                title={["Trattamenti", "Estetici Donna."]}
                subtitle="Ogni trattamento nasce dall'ascolto delle tue esigenze, per offrirti un'esperienza di benessere personalizzata in un ambiente accogliente e professionale."
                subtitleMobile="Ogni trattamento nasce dall'ascolto delle tue esigenze."
                imgDesktop={HERO_IMG.estetica.d}
                imgMobile={HERO_IMG.estetica.m}
                primaryCta={{ label: "Prenota un appuntamento", href: "/contatti" }}
                secondaryCta={{ label: "Richiedi informazioni", href: "/contatti" }}
            />

            <div className="bg-ivory pt-8 pb-4"></div>

            <Manifesto
                number="01"
                eyebrow="Il nostro tempo"
                title="Prendersi cura di sé è un gesto che merita attenzione."
                body={[
                    "Concedersi del tempo significa ritrovare equilibrio, sentirsi bene con sé stesse e dedicare attenzione al proprio benessere.",
                    "Da Spazio Isa crediamo che ogni trattamento estetico debba essere molto più di un semplice servizio. Accogliamo ogni cliente con professionalità, disponibilità e la volontà di costruire un percorso personalizzato.",
                    "Da oltre quindici anni ci prendiamo cura delle donne che scelgono di affidarsi a noi, ascoltando le loro esigenze e consigliando i trattamenti più adatti per valorizzare la bellezza naturale di ognuna.",
                ]}
            />

            <EditorialSplit
                number="02"
                eyebrow="Su misura"
                title="Trattamenti pensati per ogni esigenza."
                image={PHOTO.curaViso}
                imageAlt="Trattamenti viso"
                accentBg="bg-blush/40"
                body={[
                    "La bellezza nasce dall'attenzione ai piccoli dettagli e dalla costanza con cui ci prendiamo cura di noi stesse.",
                    "Proponiamo una selezione di trattamenti dedicati al viso, al corpo, alle mani e ai piedi, eseguiti con prodotti professionali e la massima attenzione alla qualità del servizio.",
                    "Ogni trattamento viene consigliato in base alle tue esigenze, perché ogni pelle e ogni persona sono diverse.",
                ]}
            />

            {/* Treatment cards */}
            <section className="relative py-24 md:py-32 bg-ivory-warm">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12">
                    <div className="mb-16 grid lg:grid-cols-12 gap-8">
                        <div className="lg:col-span-4">
                            <Reveal>
                                <SectionLabel number="03" text="I trattamenti" />
                            </Reveal>
                        </div>
                        <div className="lg:col-span-8">
                            <Reveal delay={0.1}>
                                <h2 className="font-display text-[32px] md:text-[46px] leading-[1.08] text-ink">
                                    Ogni donna ha esigenze diverse e merita un percorso costruito su misura.
                                </h2>
                            </Reveal>
                        </div>
                    </div>

                    <Stagger className="grid md:grid-cols-2 gap-6 lg:gap-8">
                        {treatments.map((t, i) => (
                            <motion.article
                                key={t.title}
                                variants={staggerItem}
                                className={`relative overflow-hidden rounded-[28px] ${t.color} p-8 md:p-12`}
                            >
                                <div className="flex items-start justify-between">
                                    <span className="text-ink-muted text-[15px] md:text-[17px] tabular-nums font-semibold">
                                        {t.n}
                                    </span>
                                    <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl overflow-hidden -rotate-3">
                                        <img
                                            src={t.img}
                                            alt=""
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                                <h3 className="mt-8 font-display text-[26px] md:text-[34px] leading-[1.1] text-ink">
                                    {t.title}
                                </h3>
                                <p className="mt-5 text-ink-soft text-[15px] leading-relaxed max-w-lg">
                                    {t.text}
                                </p>
                                <ul className="mt-6 flex flex-wrap gap-2">
                                    {t.services.map((s) => (
                                        <li
                                            key={s}
                                            className="text-xs uppercase tracking-[0.18em] bg-white/70 backdrop-blur px-3 py-1.5 rounded-full text-ink-soft"
                                        >
                                            {s}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/contatti"
                                    className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-semibold text-ink hover:text-raspberry transition-colors"
                                >
                                    Scopri di più
                                    <ArrowUpRight size={14} />
                                </Link>
                            </motion.article>
                        ))}
                    </Stagger>
                </div>
            </section>

            <Manifesto
                number="04"
                eyebrow="La differenza"
                title="Ciò che rende speciale l'esperienza è il rapporto con ogni cliente."
                body={[
                    "Ogni trattamento rappresenta un momento importante, ma il vero valore nasce dal rapporto che costruiamo con ogni persona.",
                    "Dedichiamo sempre il tempo necessario all'ascolto, alla consulenza e alla scelta del percorso più adatto, perché crediamo che la fiducia nasca dalla competenza, dalla disponibilità e dall'attenzione ai dettagli.",
                    "Il nostro obiettivo è farti sentire accolta, consigliata e libera di vivere ogni appuntamento come un momento dedicato esclusivamente al tuo benessere.",
                ]}
                accent="bg-rose-powder/40"
            />

            <BookingCTA
                title="Un incontro dedicato a te."
                subtitle="Ogni percorso inizia con un primo incontro, per ascoltare le tue esigenze e consigliarti il trattamento più adatto."
            />

            <FAQ items={faq} />
        </>
    );
}
